import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Pencil, Plus, ShieldCheck, ShoppingBag } from "lucide-react";
import { AnnouncementBar } from "../components/layout/AnnouncementBar";
import { Header } from "../components/layout/Header";
import { Footer } from "../components/layout/Footer";
import { Overline } from "../components/SectionHeading";
import { ImageSlot } from "../components/ImageSlot";
import { AddressFields, emptyAddress, validateAddress } from "../components/account/AddressFields";
import { formatINR } from "../data/content";
import { getProductImage } from "../data/images";
import { POLICIES, NON_AUTHORITATIVE_NOTE } from "../data/policies";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { createOrder, listAddresses, createAddress, updateAddress, validateCoupon } from "../services/api";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, itemCount, clear } = useCart();
  const { user } = useAuth();

  const [addresses, setAddresses] = useState([]);
  const [loadingAddr, setLoadingAddr] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("form"); // "select" | "form"
  const [editingId, setEditingId] = useState(null);
  const [addr, setAddr] = useState(emptyAddress);
  const [saveForFuture, setSaveForFuture] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [placed, setPlaced] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [couponInput, setCouponInput] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);

  const applyCoupon = async () => {
    setCouponMsg("");
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      const res = await validateCoupon(couponInput.trim().toUpperCase(), subtotal);
      setCouponCode(res.code);
      setCouponDiscount(res.discount || 0);
      setCouponMsg(`Coupon ${res.code} applied — you save ${formatINR(res.discount || 0)}.`);
    } catch (err) {
      setCouponCode("");
      setCouponDiscount(0);
      setCouponMsg(err?.response?.data?.detail || "Could not apply this coupon.");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
    setCouponInput("");
    setCouponMsg("");
  };

  useEffect(() => {
    if (!user) {
      setMode("form");
      return;
    }
    setLoadingAddr(true);
    listAddresses()
      .then((list) => {
        setAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) {
          setSelectedId(def.address_id);
          setMode("select");
        } else {
          setMode("form");
          setAddr({ ...emptyAddress, full_name: user.name || "" });
        }
      })
      .catch(() => setMode("form"))
      .finally(() => setLoadingAddr(false));
  }, [user]);

  const total = Math.max(subtotal - couponDiscount, 0);

  const openAddNew = () => {
    setEditingId(null);
    setErrors({});
    setAddr({ ...emptyAddress, full_name: user?.name || "" });
    setMode("form");
  };

  const openEdit = (a) => {
    setEditingId(a.address_id);
    setErrors({});
    setAddr({
      full_name: a.full_name || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      landmark: a.landmark || "",
      type: a.type || "home",
    });
    setMode("form");
  };

  const cancelForm = () => {
    setErrors({});
    setEditingId(null);
    if (addresses.length) setMode("select");
  };

  const placeOrder = async () => {
    setSubmitError("");
    let shipping;
    if (mode === "form") {
      const e = validateAddress(addr);
      setErrors(e);
      if (Object.keys(e).length) return;
      shipping = { ...addr };
      if (user) {
        try {
          if (editingId) {
            shipping = await updateAddress(editingId, { ...addr });
          } else if (saveForFuture) {
            shipping = await createAddress({ ...addr });
          }
        } catch {
          /* saving is best-effort; continue with the entered address */
        }
      }
    } else {
      shipping = addresses.find((a) => a.address_id === selectedId);
      if (!shipping) {
        setSubmitError("Please select a delivery address.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        payment_method: paymentMethod,
        coupon_code: couponCode || undefined,
        customer: {
          name: (shipping.full_name || "").trim(),
          address: {
            full_name: (shipping.full_name || "").trim(),
            line1: (shipping.line1 || "").trim(),
            line2: (shipping.line2 || "").trim() || undefined,
            city: (shipping.city || "").trim(),
            state: (shipping.state || "").trim(),
            pincode: (shipping.pincode || "").trim(),
            landmark: (shipping.landmark || "").trim() || undefined,
            type: shipping.type || "home",
            country: "India",
          },
        },
        items: items.map((l) => ({
          slug: l.slug,
          qty: l.qty,
          variant: l.variantLabels?.length
            ? Object.fromEntries(l.variantLabels.map((v) => [v.label, v.optionLabel]))
            : undefined,
        })),
      };
      const order = await createOrder(payload);
      setPlaced(order);
      clear();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(
        err?.response?.data?.detail || "We couldn't place your order just now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const summaryItems = placed?.items || items;

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="checkout-page">
      <AnnouncementBar />
      <Header />

      <main className="mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-6 lg:px-8 lg:pt-14">
        <nav className="text-[11px] uppercase tracking-[0.16em] text-brand-stone">
          <Link to="/" className="transition-colors hover:text-brand-gold">Home</Link>
          <span className="px-2 text-brand-sand">/</span>
          <span className="text-brand-charcoal">Checkout</span>
        </nav>

        {placed ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-10 max-w-2xl border border-brand-gold/30 bg-brand-surface p-8 text-center shadow-[0_30px_70px_-40px_rgba(28,25,23,0.4)] sm:p-12"
            data-testid="checkout-success"
          >
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-gold text-brand-cream">
              <Check className="h-7 w-7" strokeWidth={2} />
            </span>
            <Overline className="mt-6 justify-center">Order Confirmed</Overline>
            <h1 className="mt-4 font-serif text-3xl font-medium text-brand-charcoal sm:text-4xl">
              Thank you, {placed.customer?.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-brand-stone">
              Your bracelets are being lovingly prepared. We'll share updates on your order shortly.
            </p>
            <div className="mx-auto mt-8 max-w-sm border-t border-dashed border-brand-sand pt-6 text-left">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-stone">Order reference</span>
                <span className="font-mono text-xs font-semibold uppercase text-brand-charcoal" data-testid="checkout-order-id">
                  {placed.order_id?.slice(0, 12)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-brand-stone">Order total</span>
                <span className="font-serif text-lg font-medium text-brand-charcoal">
                  {formatINR((placed.total_paise || 0) / 100)}
                </span>
              </div>
            </div>
            <Link
              to="/shop"
              className="btn-shimmer group mt-8 inline-flex items-center gap-3 bg-brand-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-brand-cream shadow-[0_12px_30px_-12px_rgba(176,148,96,0.6)] transition-colors duration-300 hover:bg-brand-gold-hover"
              data-testid="checkout-continue-shopping"
            >
              Continue Shopping
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        ) : items.length === 0 ? (
          <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-5 text-center" data-testid="checkout-empty">
            <span className="flex h-20 w-20 items-center justify-center rounded-full border border-brand-gold/30 bg-brand-surface text-brand-gold">
              <ShoppingBag className="h-8 w-8" strokeWidth={1.4} />
            </span>
            <h1 className="font-serif text-3xl text-brand-charcoal">Your bag is empty</h1>
            <p className="max-w-xs text-sm leading-relaxed text-brand-stone">
              Add a piece that carries your aura before heading to checkout.
            </p>
            <Link
              to="/shop"
              className="btn-shimmer group mt-2 inline-flex items-center gap-3 bg-brand-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-brand-cream shadow-[0_12px_30px_-12px_rgba(176,148,96,0.6)] transition-colors duration-300 hover:bg-brand-gold-hover"
              data-testid="checkout-empty-shop"
            >
              Explore the Shop
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        ) : (
          <>
            <header className="mt-6 max-w-2xl">
              <Overline>Secure Checkout</Overline>
              <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-brand-charcoal sm:text-5xl">
                A few details, then it's yours.
              </h1>
            </header>

            <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:gap-16">
              {/* Form */}
              <form
                className="lg:col-span-7"
                onSubmit={(e) => {
                  e.preventDefault();
                  placeOrder();
                }}
                data-testid="checkout-form"
                noValidate
              >
                <section>
                  <div className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-brand-gold/50 font-serif text-sm text-brand-gold">1</span>
                    <h2 className="font-serif text-xl text-brand-charcoal">Shipping address</h2>
                  </div>

                  {user && mode === "select" ? (
                    <div className="mt-6 space-y-4" data-testid="checkout-address-list">
                      {loadingAddr ? (
                        <p className="text-sm text-brand-stone" data-testid="checkout-address-loading">Loading your addresses…</p>
                      ) : (
                        addresses.map((a) => (
                          <div
                            key={a.address_id}
                            data-testid={`checkout-address-card-${a.address_id}`}
                            className={`relative border p-5 transition-colors ${selectedId === a.address_id ? "border-brand-gold bg-brand-gold/5" : "border-brand-sand"}`}
                          >
                            <label className="flex cursor-pointer items-start gap-3 pr-16">
                              <input
                                type="radio"
                                name="ship-addr"
                                checked={selectedId === a.address_id}
                                onChange={() => setSelectedId(a.address_id)}
                                data-testid={`checkout-address-select-${a.address_id}`}
                                className="mt-1 h-4 w-4 shrink-0 accent-[#B09460]"
                              />
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-serif text-base text-brand-charcoal">{a.full_name}</span>
                                  <span className="border border-brand-gold/40 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-gold">{a.type}</span>
                                  {a.is_default && (
                                    <span data-testid={`checkout-address-default-${a.address_id}`} className="bg-brand-gold px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-cream">Default</span>
                                  )}
                                </div>
                                <p className="mt-1.5 text-sm leading-relaxed text-brand-stone">
                                  {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}
                                  {a.landmark ? ` (${a.landmark})` : ""}
                                </p>
                              </div>
                            </label>
                            <button
                              type="button"
                              onClick={() => openEdit(a)}
                              data-testid={`checkout-address-edit-${a.address_id}`}
                              className="absolute right-4 top-4 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-stone transition-colors hover:text-brand-gold"
                            >
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                          </div>
                        ))
                      )}
                      <button
                        type="button"
                        onClick={openAddNew}
                        data-testid="checkout-add-address"
                        className="inline-flex items-center gap-2 border border-brand-sand px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:border-brand-gold hover:text-brand-gold"
                      >
                        <Plus className="h-4 w-4" /> Add New Address
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6" data-testid="checkout-address-form">
                      <AddressFields value={addr} onChange={setAddr} errors={errors} idPrefix="checkout" />
                      {user && !editingId && (
                        <label className="mt-5 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-brand-stone">
                          <input
                            type="checkbox"
                            checked={saveForFuture}
                            onChange={(e) => setSaveForFuture(e.target.checked)}
                            data-testid="checkout-save-address"
                            className="mt-0.5 h-4 w-4 shrink-0 accent-[#B09460]"
                          />
                          <span>Save this address for future orders</span>
                        </label>
                      )}
                      {addresses.length > 0 && (
                        <button
                          type="button"
                          onClick={cancelForm}
                          data-testid="checkout-change-address"
                          className="mt-5 inline-flex text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone transition-colors hover:text-brand-gold"
                        >
                          ← Change / use a saved address
                        </button>
                      )}
                    </div>
                  )}
                </section>

                <section className="mt-12" data-testid="checkout-payment-section">
                  <div className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-brand-gold/50 font-serif text-sm text-brand-gold">2</span>
                    <h2 className="font-serif text-xl text-brand-charcoal">Payment method</h2>
                  </div>
                  <div className="mt-6 space-y-3">
                    <label
                      className={`flex cursor-pointer items-start gap-3 border p-4 transition-colors ${paymentMethod === "cod" ? "border-brand-gold bg-brand-gold/5" : "border-brand-sand"}`}
                      data-testid="checkout-payment-cod"
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        checked={paymentMethod === "cod"}
                        onChange={() => setPaymentMethod("cod")}
                        data-testid="checkout-payment-cod-input"
                        className="mt-1 h-4 w-4 shrink-0 accent-[#B09460]"
                      />
                      <span>
                        <span className="block font-serif text-base text-brand-charcoal">Cash on Delivery</span>
                        <span className="mt-0.5 block text-xs text-brand-stone">Pay in cash when your bracelets arrive.</span>
                      </span>
                    </label>
                    <label
                      className="flex cursor-not-allowed items-start gap-3 border border-brand-sand bg-brand-champagne/40 p-4 opacity-70"
                      data-testid="checkout-payment-online"
                    >
                      <input type="radio" name="payment-method" disabled className="mt-1 h-4 w-4 shrink-0" />
                      <span>
                        <span className="block font-serif text-base text-brand-charcoal">Online Payment</span>
                        <span className="mt-0.5 block text-xs text-brand-stone">Cards & UPI — coming soon.</span>
                      </span>
                    </label>
                  </div>
                </section>


                {submitError && (
                  <p className="mt-8 border-l-2 border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="checkout-submit-error">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-place-order"
                  className="btn-shimmer group mt-10 flex w-full items-center justify-center gap-3 bg-brand-gold py-4 text-sm font-semibold uppercase tracking-[0.18em] text-brand-cream shadow-[0_16px_34px_-14px_rgba(176,148,96,0.65)] transition-colors duration-300 hover:bg-brand-gold-hover disabled:cursor-not-allowed disabled:opacity-60 lg:hidden"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Place Order <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                </button>
              </form>

              {/* Summary */}
              <aside className="lg:col-span-5">
                <div className="lg:sticky lg:top-28">
                  <div className="border border-brand-sand bg-brand-surface p-6 sm:p-7" data-testid="checkout-summary">
                    <div className="flex items-baseline justify-between">
                      <h2 className="font-serif text-xl text-brand-charcoal">Order summary</h2>
                      <span className="text-[11px] uppercase tracking-[0.16em] text-brand-stone">
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </span>
                    </div>

                    <div className="mt-5 divide-y divide-brand-sand/80 border-t border-brand-sand/80">
                      {summaryItems.map((line) => {
                        const img = getProductImage(line.image, line.name);
                        const qty = line.qty;
                        const lineTotal = line.line_total_paise != null ? line.line_total_paise / 100 : line.unitPrice * line.qty;
                        return (
                          <div key={line.key || line.slug} className="flex gap-4 py-4" data-testid="checkout-line-item">
                            <div className="w-16 shrink-0">
                              <div className="overflow-hidden border border-brand-sand bg-brand-champagne">
                                <ImageSlot name={`co-${line.key || line.slug}`} label={line.name} src={img.src} alt={img.alt} ratio="aspect-[4/5]" />
                              </div>
                            </div>
                            <div className="flex flex-1 flex-col">
                              <p className="font-serif text-sm leading-snug text-brand-charcoal">{line.name}</p>
                              {line.variantLabels?.length > 0 && (
                                <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-brand-stone">
                                  {line.variantLabels.map((v) => `${v.label}: ${v.optionLabel}`).join(" · ")}
                                </p>
                              )}
                              <div className="mt-auto flex items-center justify-between pt-2 text-sm">
                                <span className="text-brand-stone">Qty {qty}</span>
                                <span className="font-serif tabular-nums text-brand-charcoal">{formatINR(lineTotal)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-5 border-t border-brand-sand pt-4" data-testid="checkout-coupon">
                      {couponCode ? (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm text-brand-charcoal">
                            Coupon <span className="font-mono font-semibold text-brand-gold">{couponCode}</span> applied
                          </p>
                          <button type="button" onClick={removeCoupon} data-testid="checkout-coupon-remove" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-stone hover:text-red-600">Remove</button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            placeholder="Coupon code"
                            data-testid="checkout-coupon-input"
                            className="flex-1 border border-brand-sand bg-brand-surface px-4 py-2.5 text-sm uppercase tracking-wide text-brand-charcoal outline-none focus:border-brand-gold"
                          />
                          <button type="button" onClick={applyCoupon} disabled={couponBusy} data-testid="checkout-coupon-apply" className="border border-brand-charcoal px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-brand-cream disabled:opacity-50">
                            {couponBusy ? "…" : "Apply"}
                          </button>
                        </div>
                      )}
                      {couponMsg && <p className="mt-2 text-[11px] text-brand-stone" data-testid="checkout-coupon-msg">{couponMsg}</p>}
                    </div>

                    <div className="mt-4 space-y-2 border-t border-brand-sand pt-4 text-sm">
                      <div className="flex items-center justify-between text-brand-stone">
                        <span>Subtotal</span>
                        <span className="tabular-nums text-brand-charcoal" data-testid="checkout-subtotal">{formatINR(subtotal)}</span>
                      </div>
                      {couponDiscount > 0 && (
                        <div className="flex items-center justify-between text-brand-stone">
                          <span>Coupon discount</span>
                          <span className="tabular-nums text-brand-gold" data-testid="checkout-discount">-{formatINR(couponDiscount)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-brand-stone">
                        <span>Shipping</span>
                        <span>Calculated at checkout</span>
                      </div>
                      <div className="flex items-baseline justify-between border-t border-brand-sand/70 pt-3 text-brand-charcoal">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">Estimated total</span>
                        <span className="font-serif text-2xl font-medium tabular-nums" data-testid="checkout-total">{formatINR(total)}</span>
                      </div>
                    </div>

                    <p className="mt-4 flex items-center gap-2 text-[11px] text-brand-stone">
                      <ShieldCheck className="h-3.5 w-3.5 text-brand-gold" />
                      No payment is taken now — an order request is created.
                    </p>

                    <button
                      type="button"
                      onClick={placeOrder}
                      disabled={submitting}
                      data-testid="checkout-place-order-desktop"
                      className="btn-shimmer group mt-5 hidden w-full items-center justify-center gap-3 bg-brand-gold py-4 text-sm font-semibold uppercase tracking-[0.18em] text-brand-cream shadow-[0_16px_34px_-14px_rgba(176,148,96,0.65)] transition-colors duration-300 hover:bg-brand-gold-hover disabled:cursor-not-allowed disabled:opacity-60 lg:flex"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Place Order <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                    </button>

                    <p className="mt-4 text-[10px] leading-relaxed text-brand-stone/80">{NON_AUTHORITATIVE_NOTE}</p>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
